import {
  mockHealthIncidents,
  mockSafetyProtocols,
  mockEmergencyContacts,
  mockHealthCheckups,
} from '../../__mocks__/administration';
import { createHttpService } from '../ApiService';
import { normalizePaginatedResponse } from '../__shared/paginationUtils';
import {
  THealthIncident,
  THealthIncidentListApiResponse,
  THealthIncidentParams,
  TSafetyProtocol,
  TSafetyProtocolListApiResponse,
  TSafetyProtocolParams,
  TEmergencyContact,
  TEmergencyContactListApiResponse,
  TEmergencyContactParams,
  THealthCheckup,
  THealthCheckupListApiResponse,
  THealthCheckupParams,
} from './healthAndSafety.api.interfaces';
import isAppWithMock from 'src/utils/isAppWithMock';

export const useHealthAndSafetyService = () => {
  const httpAPI = createHttpService();
  const baseHealthPath = '/health';
  const withMockData = isAppWithMock();

  // ===================== HEALTH INCIDENTS =====================

  const getHealthIncidentList = (
    _params: THealthIncidentParams
  ): Promise<THealthIncidentListApiResponse> => {
    if (withMockData) {
      // Apply filtering for mock data
      let filteredIncidents = [...mockHealthIncidents];

      if (_params.severity) {
        filteredIncidents = filteredIncidents.filter(
          (incident) => incident.severity === _params.severity
        );
      }

      if (_params.status) {
        filteredIncidents = filteredIncidents.filter(
          (incident) => incident.status === _params.status
        );
      }

      if (_params.incidentType) {
        filteredIncidents = filteredIncidents.filter(
          (incident) => incident.incidentType === _params.incidentType
        );
      }

      if (_params.personType) {
        filteredIncidents = filteredIncidents.filter(
          (incident) => incident.affectedPersonType === _params.personType
        );
      }

      if (_params.personId) {
        filteredIncidents = filteredIncidents.filter(
          (incident) => incident.affectedPersonId === _params.personId
        );
      }

      // Date range filtering
      if (_params.fromDate) {
        const fromDate = new Date(_params.fromDate);
        filteredIncidents = filteredIncidents.filter(
          (incident) => incident.incidentDate >= fromDate
        );
      }

      if (_params.toDate) {
        const toDate = new Date(_params.toDate);
        filteredIncidents = filteredIncidents.filter(
          (incident) => incident.incidentDate <= toDate
        );
      }

      // Pagination
      const page = Number(_params.page) || 1;
      const limit = Number(_params.limit) || 20;
      const startIndex = (page - 1) * limit;
      const paginatedIncidents = filteredIncidents.slice(
        startIndex,
        startIndex + limit
      );

      return Promise.resolve(
        normalizePaginatedResponse(
          { data: paginatedIncidents, count: filteredIncidents.length, page, limit },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 20,
        ...(_params.severity && { severity: _params.severity }),
        ...(_params.status && { status: _params.status }),
        ...(_params.incidentType && { incidentType: _params.incidentType }),
        ...(_params.personType && { personType: _params.personType }),
        ...(_params.personId && { personId: _params.personId }),
        ...(_params.fromDate && { fromDate: _params.fromDate }),
        ...(_params.toDate && { toDate: _params.toDate }),
        ...(_params.instituteId && { instituteId: _params.instituteId }),
      },
    };

    return httpAPI.admin
      .get<any>(`${baseHealthPath}/incidents`, requestObj)
      .then((res) => {
        const body = res?.data ?? res;
        const rawData = body?.data ?? [];
        const dataWithDates = Array.isArray(rawData)
          ? rawData.map((incident: any) => ({
              ...incident,
              reportedDate: new Date(incident.reportedDate),
              incidentDate: new Date(incident.incidentDate),
              followUpDate: incident.followUpDate
                ? new Date(incident.followUpDate)
                : undefined,
              createdAt: incident.createdAt
                ? new Date(incident.createdAt)
                : undefined,
              updatedAt: incident.updatedAt
                ? new Date(incident.updatedAt)
                : undefined,
            }))
          : [];
        return normalizePaginatedResponse<THealthIncident>(
          { ...body, data: dataWithDates },
          'data'
        );
      })
      .catch((error) => {
        console.error('Error fetching health incidents:', error);
        throw error;
      });
  };

  const getHealthIncidentById = (id: string): Promise<THealthIncident> => {
    if (withMockData) {
      const incident = mockHealthIncidents.find(
        (incident) => incident.id === id
      );
      if (incident) {
        return Promise.resolve(incident);
      }
      return Promise.reject(new Error('Health incident not found'));
    }

    return httpAPI.admin
      .get<any>(`${baseHealthPath}/incidents/${id}`)
      .then((response) => {
        // Convert date strings to Date objects
        return {
          ...response,
          reportedDate: new Date(response.reportedDate),
          incidentDate: new Date(response.incidentDate),
          followUpDate: response.followUpDate
            ? new Date(response.followUpDate)
            : undefined,
          createdAt: response.createdAt
            ? new Date(response.createdAt)
            : undefined,
          updatedAt: response.updatedAt
            ? new Date(response.updatedAt)
            : undefined,
        };
      })
      .catch((error) => {
        console.error('Error fetching health incident:', error);
        return Promise.reject(error);
      });
  };

  const createHealthIncident = (
    incident: Omit<THealthIncident, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<THealthIncident> => {
    if (withMockData) {
      const newIncident: THealthIncident = {
        ...incident,
        id: `incident-${Date.now()}`,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      return Promise.resolve(newIncident);
    }

    // Convert Date objects to ISO strings for API
    const apiPayload = {
      ...incident,
      reportedDate: incident.reportedDate.toISOString(),
      incidentDate: incident.incidentDate.toISOString(),
      followUpDate: incident.followUpDate?.toISOString(),
    };

    return httpAPI.admin
      .post<any>(`${baseHealthPath}/incidents`, apiPayload)
      .then((response) => ({
        ...response,
        reportedDate: new Date(response.reportedDate),
        incidentDate: new Date(response.incidentDate),
        followUpDate: response.followUpDate
          ? new Date(response.followUpDate)
          : undefined,
        createdAt: response.createdAt
          ? new Date(response.createdAt)
          : undefined,
        updatedAt: response.updatedAt
          ? new Date(response.updatedAt)
          : undefined,
      }));
  };

  const updateHealthIncident = (
    id: string,
    incident: Partial<THealthIncident>
  ): Promise<THealthIncident> => {
    if (withMockData) {
      const existingIncident = mockHealthIncidents.find((i) => i.id === id);
      if (existingIncident) {
        const updatedIncident = {
          ...existingIncident,
          ...incident,
          updatedAt: new Date(),
        };
        return Promise.resolve(updatedIncident);
      }
      return Promise.reject(new Error('Health incident not found'));
    }

    // Convert Date objects to ISO strings for API
    const apiPayload: any = { ...incident };
    if (incident.reportedDate)
      apiPayload.reportedDate = incident.reportedDate.toISOString();
    if (incident.incidentDate)
      apiPayload.incidentDate = incident.incidentDate.toISOString();
    if (incident.followUpDate)
      apiPayload.followUpDate = incident.followUpDate.toISOString();

    return httpAPI.admin
      .patch<any>(`${baseHealthPath}/incidents/${id}`, apiPayload)
      .then((response) => ({
        ...response,
        reportedDate: new Date(response.reportedDate),
        incidentDate: new Date(response.incidentDate),
        followUpDate: response.followUpDate
          ? new Date(response.followUpDate)
          : undefined,
        createdAt: response.createdAt
          ? new Date(response.createdAt)
          : undefined,
        updatedAt: response.updatedAt
          ? new Date(response.updatedAt)
          : undefined,
      }));
  };

  const deleteHealthIncident = (id: string): Promise<void> => {
    if (withMockData) {
      return Promise.resolve();
    }

    return httpAPI.admin.delete<void>(`${baseHealthPath}/incidents/${id}`);
  };

  // ===================== SAFETY PROTOCOLS =====================

  const getSafetyProtocolList = (
    _params: TSafetyProtocolParams
  ): Promise<TSafetyProtocolListApiResponse> => {
    if (withMockData) {
      let filteredProtocols = [...mockSafetyProtocols];

      if (_params.category) {
        filteredProtocols = filteredProtocols.filter(
          (protocol) => protocol.category === _params.category
        );
      }

      if (_params.isActive !== undefined) {
        filteredProtocols = filteredProtocols.filter(
          (protocol) => protocol.isActive === _params.isActive
        );
      }

      if (_params.trainingRequired !== undefined) {
        filteredProtocols = filteredProtocols.filter(
          (protocol) => protocol.trainingRequired === _params.trainingRequired
        );
      }

      const page = Number(_params.page) || 1;
      const limit = Number(_params.limit) || 20;
      const startIndex = (page - 1) * limit;
      const paginatedProtocols = filteredProtocols.slice(
        startIndex,
        startIndex + limit
      );

      return Promise.resolve(
        normalizePaginatedResponse(
          { data: paginatedProtocols, count: filteredProtocols.length, page, limit },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 20,
        ...(_params.category && { category: _params.category }),
        ...(_params.isActive !== undefined && {
          isActive: _params.isActive,
        }),
        ...(_params.trainingRequired !== undefined && {
          trainingRequired: _params.trainingRequired,
        }),
        ...(_params.instituteId && { instituteId: _params.instituteId }),
      },
    };

    return httpAPI.admin
      .get<any>(`${baseHealthPath}/protocols`, requestObj)
      .then((res) => {
        const body = res?.data ?? res;
        const rawData = body?.data ?? [];
        const dataWithDates = Array.isArray(rawData)
          ? rawData.map((protocol: any) => ({
              ...protocol,
              lastUpdated: new Date(protocol.lastUpdated),
              nextReviewDate: new Date(protocol.nextReviewDate),
              lastTrainingDate: protocol.lastTrainingDate
                ? new Date(protocol.lastTrainingDate)
                : undefined,
              nextTrainingDate: protocol.nextTrainingDate
                ? new Date(protocol.nextTrainingDate)
                : undefined,
              createdAt: protocol.createdAt
                ? new Date(protocol.createdAt)
                : undefined,
              updatedAt: protocol.updatedAt
                ? new Date(protocol.updatedAt)
                : undefined,
            }))
          : [];
        return normalizePaginatedResponse<TSafetyProtocol>(
          { ...body, data: dataWithDates },
          'data'
        );
      })
      .catch((error) => {
        console.error('Error fetching safety protocols:', error);
        return getSafetyProtocolList({ ..._params });
      });
  };

  const getSafetyProtocolById = (id: string): Promise<TSafetyProtocol> => {
    if (withMockData) {
      const protocol = mockSafetyProtocols.find(
        (protocol) => protocol.id === id
      );
      if (protocol) {
        return Promise.resolve(protocol);
      }
      return Promise.reject(new Error('Safety protocol not found'));
    }

    return httpAPI.admin
      .get<any>(`${baseHealthPath}/protocols/${id}`)
      .then((response) => ({
        ...response,
        lastUpdated: new Date(response.lastUpdated),
        nextReviewDate: new Date(response.nextReviewDate),
        lastTrainingDate: response.lastTrainingDate
          ? new Date(response.lastTrainingDate)
          : undefined,
        nextTrainingDate: response.nextTrainingDate
          ? new Date(response.nextTrainingDate)
          : undefined,
        createdAt: response.createdAt
          ? new Date(response.createdAt)
          : undefined,
        updatedAt: response.updatedAt
          ? new Date(response.updatedAt)
          : undefined,
      }))
      .catch((error) => {
        console.error('Error fetching safety protocol:', error);
        return getSafetyProtocolById(id);
      });
  };

  const createSafetyProtocol = (
    protocol: Omit<TSafetyProtocol, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<TSafetyProtocol> => {
    if (withMockData) {
      const newProtocol: TSafetyProtocol = {
        ...protocol,
        id: `protocol-${Date.now()}`,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      return Promise.resolve(newProtocol);
    }

    const apiPayload = {
      ...protocol,
      lastUpdated: protocol.lastUpdated.toISOString(),
      nextReviewDate: protocol.nextReviewDate.toISOString(),
      lastTrainingDate: protocol.lastTrainingDate?.toISOString(),
      nextTrainingDate: protocol.nextTrainingDate?.toISOString(),
    };

    return httpAPI.admin
      .post<any>(`${baseHealthPath}/protocols`, apiPayload)
      .then((response) => ({
        ...response,
        lastUpdated: new Date(response.lastUpdated),
        nextReviewDate: new Date(response.nextReviewDate),
        lastTrainingDate: response.lastTrainingDate
          ? new Date(response.lastTrainingDate)
          : undefined,
        nextTrainingDate: response.nextTrainingDate
          ? new Date(response.nextTrainingDate)
          : undefined,
        createdAt: response.createdAt
          ? new Date(response.createdAt)
          : undefined,
        updatedAt: response.updatedAt
          ? new Date(response.updatedAt)
          : undefined,
      }));
  };

  const updateSafetyProtocol = (
    id: string,
    protocol: Partial<TSafetyProtocol>
  ): Promise<TSafetyProtocol> => {
    if (withMockData) {
      const existingProtocol = mockSafetyProtocols.find((p) => p.id === id);
      if (existingProtocol) {
        const updatedProtocol = {
          ...existingProtocol,
          ...protocol,
          updatedAt: new Date(),
        };
        return Promise.resolve(updatedProtocol);
      }
      return Promise.reject(new Error('Safety protocol not found'));
    }

    const apiPayload: any = { ...protocol };
    if (protocol.lastUpdated)
      apiPayload.lastUpdated = protocol.lastUpdated.toISOString();
    if (protocol.nextReviewDate)
      apiPayload.nextReviewDate = protocol.nextReviewDate.toISOString();
    if (protocol.lastTrainingDate)
      apiPayload.lastTrainingDate = protocol.lastTrainingDate.toISOString();
    if (protocol.nextTrainingDate)
      apiPayload.nextTrainingDate = protocol.nextTrainingDate.toISOString();

    return httpAPI.admin
      .patch<any>(`${baseHealthPath}/protocols/${id}`, apiPayload)
      .then((response) => ({
        ...response,
        lastUpdated: new Date(response.lastUpdated),
        nextReviewDate: new Date(response.nextReviewDate),
        lastTrainingDate: response.lastTrainingDate
          ? new Date(response.lastTrainingDate)
          : undefined,
        nextTrainingDate: response.nextTrainingDate
          ? new Date(response.nextTrainingDate)
          : undefined,
        createdAt: response.createdAt
          ? new Date(response.createdAt)
          : undefined,
        updatedAt: response.updatedAt
          ? new Date(response.updatedAt)
          : undefined,
      }));
  };

  const deleteSafetyProtocol = (id: string): Promise<void> => {
    if (withMockData) {
      return Promise.resolve();
    }

    return httpAPI.admin.delete<void>(`${baseHealthPath}/protocols/${id}`);
  };

  // ===================== EMERGENCY CONTACTS =====================

  const getEmergencyContactList = (
    _params: TEmergencyContactParams
  ): Promise<TEmergencyContactListApiResponse> => {
    if (withMockData) {
      let filteredContacts = [...mockEmergencyContacts];

      if (_params.personType) {
        filteredContacts = filteredContacts.filter(
          (contact) => contact.personType === _params.personType
        );
      }

      if (_params.personId) {
        filteredContacts = filteredContacts.filter(
          (contact) => contact.personId === _params.personId
        );
      }

      if (_params.isActive !== undefined) {
        filteredContacts = filteredContacts.filter(
          (contact) => contact.isActive === _params.isActive
        );
      }

      if (_params.priority) {
        filteredContacts = filteredContacts.filter(
          (contact) => contact.priority === _params.priority
        );
      }

      const page = Number(_params.page) || 1;
      const limit = Number(_params.limit) || 20;
      const startIndex = (page - 1) * limit;
      const paginatedContacts = filteredContacts.slice(
        startIndex,
        startIndex + limit
      );

      return Promise.resolve(
        normalizePaginatedResponse(
          { data: paginatedContacts, count: filteredContacts.length, page, limit },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 20,
        ...(_params.personType && { personType: _params.personType }),
        ...(_params.personId && { personId: _params.personId }),
        ...(_params.isActive !== undefined && { isActive: _params.isActive }),
        ...(_params.priority && { priority: _params.priority }),
        ...(_params.instituteId && { instituteId: _params.instituteId }),
      },
    };

    return httpAPI.admin
      .get<any>(`${baseHealthPath}/emergency-contacts`, requestObj)
      .then((res) => {
        const body = res?.data ?? res;
        const rawData = body?.data ?? [];
        const dataWithDates = Array.isArray(rawData)
          ? rawData.map((contact: any) => ({
              ...contact,
              createdAt: contact.createdAt
                ? new Date(contact.createdAt)
                : undefined,
              updatedAt: contact.updatedAt
                ? new Date(contact.updatedAt)
                : undefined,
            }))
          : [];
        return normalizePaginatedResponse<TEmergencyContact>(
          { ...body, data: dataWithDates },
          'data'
        );
      })
      .catch((error) => {
        console.error('Error fetching emergency contacts:', error);
        return getEmergencyContactList({ ..._params });
      });
  };

  const getEmergencyContactById = (id: string): Promise<TEmergencyContact> => {
    if (withMockData) {
      const contact = mockEmergencyContacts.find(
        (contact) => contact.id === id
      );
      if (contact) {
        return Promise.resolve(contact);
      }
      return Promise.reject(new Error('Emergency contact not found'));
    }

    return httpAPI.admin
      .get<any>(`${baseHealthPath}/emergency-contacts/${id}`)
      .then((response) => ({
        ...response,
        createdAt: response.createdAt
          ? new Date(response.createdAt)
          : undefined,
        updatedAt: response.updatedAt
          ? new Date(response.updatedAt)
          : undefined,
      }))
      .catch((error) => {
        console.error('Error fetching emergency contact:', error);
        return getEmergencyContactById(id);
      });
  };

  const createEmergencyContact = (
    contact: Omit<TEmergencyContact, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<TEmergencyContact> => {
    if (withMockData) {
      const newContact: TEmergencyContact = {
        ...contact,
        id: `contact-${Date.now()}`,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      return Promise.resolve(newContact);
    }

    return httpAPI.admin
      .post<any>(`${baseHealthPath}/emergency-contacts`, contact)
      .then((response) => ({
        ...response,
        createdAt: response.createdAt
          ? new Date(response.createdAt)
          : undefined,
        updatedAt: response.updatedAt
          ? new Date(response.updatedAt)
          : undefined,
      }));
  };

  const updateEmergencyContact = (
    id: string,
    contact: Partial<TEmergencyContact>
  ): Promise<TEmergencyContact> => {
    if (withMockData) {
      const existingContact = mockEmergencyContacts.find((c) => c.id === id);
      if (existingContact) {
        const updatedContact = {
          ...existingContact,
          ...contact,
          updatedAt: new Date(),
        };
        return Promise.resolve(updatedContact);
      }
      return Promise.reject(new Error('Emergency contact not found'));
    }

    return httpAPI.admin
      .patch<any>(`${baseHealthPath}/emergency-contacts/${id}`, contact)
      .then((response) => ({
        ...response,
        createdAt: response.createdAt
          ? new Date(response.createdAt)
          : undefined,
        updatedAt: response.updatedAt
          ? new Date(response.updatedAt)
          : undefined,
      }));
  };

  const deleteEmergencyContact = (id: string): Promise<void> => {
    if (withMockData) {
      return Promise.resolve();
    }

    return httpAPI.admin.delete<void>(
      `${baseHealthPath}/emergency-contacts/${id}`
    );
  };

  // ===================== HEALTH CHECKUPS =====================

  // Helper to normalize attachments from TDocument[] to string[]
  const normalizeAttachments = (attachments: any): string[] | undefined => {
    if (!attachments) return undefined;
    if (Array.isArray(attachments)) {
      return attachments.map((item) =>
        typeof item === 'string' ? item : item.documentId || item.fileUrl || ''
      );
    }
    return undefined;
  };

  // Helper to normalize a checkup object's attachments
  const normalizeCheckup = (checkup: any): THealthCheckup => ({
    ...checkup,
    attachments: normalizeAttachments(checkup.attachments),
  });

  const getHealthCheckupList = (
    _params: THealthCheckupParams
  ): Promise<THealthCheckupListApiResponse> => {
    if (withMockData) {
      let filteredCheckups = [...mockHealthCheckups];

      if (_params.personType) {
        filteredCheckups = filteredCheckups.filter(
          (checkup) => checkup.personType === _params.personType
        );
      }

      if (_params.personId) {
        filteredCheckups = filteredCheckups.filter(
          (checkup) => checkup.personId === _params.personId
        );
      }

      if (_params.checkupType) {
        filteredCheckups = filteredCheckups.filter(
          (checkup) => checkup.checkupType === _params.checkupType
        );
      }

      if (_params.fromDate) {
        const fromDate = new Date(_params.fromDate);
        filteredCheckups = filteredCheckups.filter(
          (checkup) => checkup.checkupDate >= fromDate
        );
      }

      if (_params.toDate) {
        const toDate = new Date(_params.toDate);
        filteredCheckups = filteredCheckups.filter(
          (checkup) => checkup.checkupDate <= toDate
        );
      }

      const page = Number(_params.page) || 1;
      const limit = Number(_params.limit) || 20;
      const startIndex = (page - 1) * limit;
      const paginatedCheckups = filteredCheckups.slice(
        startIndex,
        startIndex + limit
      );

      return Promise.resolve(
        normalizePaginatedResponse(
          {
            data: paginatedCheckups.map(normalizeCheckup),
            count: filteredCheckups.length,
            page,
            limit,
          },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 20,
        ...(_params.personType && { personType: _params.personType }),
        ...(_params.personId && { personId: _params.personId }),
        ...(_params.checkupType && { checkupType: _params.checkupType }),
        ...(_params.fromDate && { fromDate: _params.fromDate }),
        ...(_params.toDate && { toDate: _params.toDate }),
        ...(_params.instituteId && { instituteId: _params.instituteId }),
      },
    };

    return httpAPI.admin
      .get<any>(`${baseHealthPath}/checkups`, requestObj)
      .then((res) => {
        const body = res?.data ?? res;
        const rawData = body?.data ?? [];
        const dataWithDates = Array.isArray(rawData)
          ? rawData.map((checkup: any) => ({
              ...checkup,
              checkupDate: new Date(checkup.checkupDate),
              nextCheckupDate: checkup.nextCheckupDate
                ? new Date(checkup.nextCheckupDate)
                : undefined,
              createdAt: checkup.createdAt
                ? new Date(checkup.createdAt)
                : undefined,
              updatedAt: checkup.updatedAt
                ? new Date(checkup.updatedAt)
                : undefined,
            }))
          : [];
        return normalizePaginatedResponse<THealthCheckup>(
          { ...body, data: dataWithDates },
          'data'
        );
      })
      .catch((error) => {
        console.error('Error fetching health checkups:', error);
        return getHealthCheckupList({ ..._params });
      });
  };

  const getHealthCheckupById = (id: string): Promise<THealthCheckup> => {
    if (withMockData) {
      const checkup = mockHealthCheckups.find((checkup) => checkup.id === id);
      if (checkup) {
        return Promise.resolve(normalizeCheckup(checkup));
      }
      return Promise.reject(new Error('Health checkup not found'));
    }

    return httpAPI.admin
      .get<any>(`${baseHealthPath}/checkups/${id}`)
      .then((response) => ({
        ...response,
        checkupDate: new Date(response.checkupDate),
        nextCheckupDate: response.nextCheckupDate
          ? new Date(response.nextCheckupDate)
          : undefined,
        createdAt: response.createdAt
          ? new Date(response.createdAt)
          : undefined,
        updatedAt: response.updatedAt
          ? new Date(response.updatedAt)
          : undefined,
      }))
      .catch((error) => {
        console.error('Error fetching health checkup:', error);
        return getHealthCheckupById(id);
      });
  };

  const createHealthCheckup = (
    checkup: Omit<THealthCheckup, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<THealthCheckup> => {
    if (withMockData) {
      const newCheckup: THealthCheckup = {
        ...checkup,
        attachments: normalizeAttachments(checkup.attachments),
        id: `checkup-${Date.now()}`,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      return Promise.resolve(newCheckup);
    }

    const apiPayload = {
      ...checkup,
      checkupDate: checkup.checkupDate.toISOString(),
      nextCheckupDate: checkup.nextCheckupDate?.toISOString(),
    };

    return httpAPI.admin
      .post<any>(`${baseHealthPath}/checkups`, apiPayload)
      .then((response) => ({
        ...response,
        checkupDate: new Date(response.checkupDate),
        nextCheckupDate: response.nextCheckupDate
          ? new Date(response.nextCheckupDate)
          : undefined,
        createdAt: response.createdAt
          ? new Date(response.createdAt)
          : undefined,
        updatedAt: response.updatedAt
          ? new Date(response.updatedAt)
          : undefined,
      }));
  };

  const updateHealthCheckup = (
    id: string,
    checkup: Partial<THealthCheckup>
  ): Promise<THealthCheckup> => {
    if (withMockData) {
      const existingCheckup = mockHealthCheckups.find((c) => c.id === id);
      if (existingCheckup) {
        const updatedCheckup = {
          ...existingCheckup,
          ...checkup,
          attachments:
            checkup.attachments !== undefined
              ? normalizeAttachments(checkup.attachments)
              : normalizeAttachments(existingCheckup.attachments),
          updatedAt: new Date(),
        };
        return Promise.resolve(normalizeCheckup(updatedCheckup));
      }
      return Promise.reject(new Error('Health checkup not found'));
    }

    const apiPayload: any = { ...checkup };
    if (checkup.checkupDate)
      apiPayload.checkupDate = checkup.checkupDate.toISOString();
    if (checkup.nextCheckupDate)
      apiPayload.nextCheckupDate = checkup.nextCheckupDate.toISOString();

    return httpAPI.admin
      .patch<any>(`${baseHealthPath}/checkups/${id}`, apiPayload)
      .then((response) => ({
        ...response,
        checkupDate: new Date(response.checkupDate),
        nextCheckupDate: response.nextCheckupDate
          ? new Date(response.nextCheckupDate)
          : undefined,
        createdAt: response.createdAt
          ? new Date(response.createdAt)
          : undefined,
        updatedAt: response.updatedAt
          ? new Date(response.updatedAt)
          : undefined,
      }));
  };

  const deleteHealthCheckup = (id: string): Promise<void> => {
    if (withMockData) {
      return Promise.resolve();
    }

    return httpAPI.admin.delete<void>(`${baseHealthPath}/checkups/${id}`);
  };

  return {
    // Health Incidents
    getHealthIncidentList,
    getHealthIncidentById,
    createHealthIncident,
    updateHealthIncident,
    deleteHealthIncident,
    // Safety Protocols
    getSafetyProtocolList,
    getSafetyProtocolById,
    createSafetyProtocol,
    updateSafetyProtocol,
    deleteSafetyProtocol,
    // Emergency Contacts
    getEmergencyContactList,
    getEmergencyContactById,
    createEmergencyContact,
    updateEmergencyContact,
    deleteEmergencyContact,
    // Health Checkups
    getHealthCheckupList,
    getHealthCheckupById,
    createHealthCheckup,
    updateHealthCheckup,
    deleteHealthCheckup,
  };
};
